import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, TextField, Typography, useTheme } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useMediaQuery } from "@mui/material";
import { useDispatch } from "react-redux";

import { setCredentials } from "./authSlice";
import { useLoginMutation } from "./authApiSlice";
import { useSendOTPMutation, useForgotPasswordMutation } from "./apiSlice";
import { tokens } from "../../theme";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  contact: "",
  password: "",
  cnfPassword: "",
  otp: "",
  newPassword: "",
};

const userSchema = yup.object().shape({
  email: yup.string().email("invalid email").required("required"),
  password: yup.string().required("required"),
});

interface Passwords {
  password: string;
  cnfPassword: string;
  otp: string;
  hash: string;
}

const Login = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [errMsg, setErrMsg] = useState("");
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [email, setEmail] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [sendOTPAPI] = useSendOTPMutation();
  const [passwords, setPasswords] = useState({
    cnfPassword: "",
    otp: "",
    password: "",
    hash: "",
  } as Passwords);
  const [foorgotPasswordAPI] = useForgotPasswordMutation();

  const handleSubmit = async (values: any) => {
    try {
      const userData = await login(values).unwrap();
      dispatch(
        setCredentials({
          accessToken: userData?.token,
          sponserId: userData?.sponserId,
          name: userData?.name,
          email: userData?.email,
          profileImageUrl: userData?.imageUrl,
          notifications: userData?.notifications,
        })
      );
      const updatedUserData = {
        ...userData,
        accessToken: userData?.token,
        profileImageUrl: userData?.imageUrl,
        notifications: userData?.notifications,
      };
      localStorage.setItem("userData", JSON.stringify(updatedUserData));
      navigate("/dashboard");
    } catch (err: any) {
      setErrMsg(err.data.message);
    }
  };

  const handleForgorPasswordClick = async () => {
    if (email === "") {
      setErrMsg("Email is required!");
      return;
    }
    setErrMsg("");
    const response: any = await sendOTPAPI({ email }).unwrap();
    if (response.status === 200) {
      setErrMsg("OTP Sent!");
      setPasswords({ ...passwords, hash: response.hash });
      setForgotPassword(true);
    }
  };

  const handleSubmitClicked = async () => {
    if (!forgotPassword) {
      return;
    }
    if (passwords.otp === "") {
      setErrMsg("OTP is required!");
      return;
    }
    if (passwords.password !== passwords.cnfPassword) {
      setErrMsg("Passwords do not match!");
      return;
    }
    const response: any = await foorgotPasswordAPI({
      otp: passwords.otp,
      hash: passwords.hash,
      email: email,
      password: passwords.password,
    }).unwrap();
    if (response.status === 200) {
      setForgotPassword(false);
      setErrMsg("");
    }
  };

  return isLoading ? (
    <div className="flex items-center justify-center">
      <h1>Loading...</h1>
    </div>
  ) : (
    <div
      className={`
      flex 
      flex-col 
      justify-center 
      items-center 
      h-[100%]
    `}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={userSchema}
      >
        {/* @ts-ignore */}
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Box
                gap="30px"
                sx={{
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4",
                  },
                }}
              >
                {!forgotPassword ? (
                  <>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={"text"}
                      label="E-Mail"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        setEmail(e.target.value);
                      }}
                      value={values.email}
                      name="email"
                      error={!!touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                      sx={{ gridColumn: "span 12" }}
                    />

                    <TextField
                      fullWidth
                      variant="outlined"
                      type={"password"}
                      label="Password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.password}
                      name="password"
                      error={!!touched.password && !!errors.password}
                      helperText={touched.password && errors.password}
                      sx={{ gridColumn: "span 12", mt: "12px", mb: "12px" }}
                    />
                    <Typography
                      variant="h5"
                      style={{ color: colors.redAccent[400] }}
                      className={"hover:cursor-pointer select-none"}
                    >
                      <span onClick={handleForgorPasswordClick}>
                        Forgot Password ?
                      </span>
                    </Typography>
                  </>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={"text"}
                      label="E-Mail"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        setEmail(e.target.value);
                      }}
                      value={values.email}
                      name="email"
                      error={!!touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                      sx={{ gridColumn: "span 12" }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={"password"}
                      label="OTP"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        setPasswords({ ...passwords, otp: e.target.value });
                      }}
                      value={values.otp}
                      name="otp"
                      error={!!touched.otp && !!errors.otp}
                      helperText={touched.otp && errors.otp}
                      sx={{ gridColumn: "span 12", mt: "12px", mb: "12px" }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={"password"}
                      label="Password"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        setPasswords({
                          ...passwords,
                          password: e.target.value,
                        });
                      }}
                      value={values.newPassword}
                      name="newPassword"
                      error={!!touched.newPassword && !!errors.newPassword}
                      helperText={touched.newPassword && errors.newPassword}
                      sx={{ gridColumn: "span 12", mt: "12px", mb: "12px" }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      type={"password"}
                      label="Confirm Password"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        setPasswords({
                          ...passwords,
                          cnfPassword: e.target.value,
                        });
                      }}
                      value={values.cnfPassword}
                      name="cnfPassword"
                      error={!!touched.cnfPassword && !!errors.cnfPassword}
                      helperText={touched.cnfPassword && errors.cnfPassword}
                      sx={{ gridColumn: "span 12", mt: "12px", mb: "12px" }}
                    />
                  </>
                )}
              </Box>
              <Box display="flex" justifyContent={"center"} mt="20px">
                {forgotPassword ? (
                  <Button
                    color="secondary"
                    variant="contained"
                    fullWidth
                    onClick={handleSubmitClicked}
                  >
                    Reset Password
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    fullWidth
                  >
                    Login
                  </Button>
                )}
              </Box>
              <Typography variant="h4" align="center" color={"red"}>
                {errMsg}
              </Typography>
              <div className="mt-5 flex w-[100%] justify-between items-center">
                <div className="flex-1">
                  <hr />
                </div>
                <div>OR</div>
                <div className="flex-1">
                  <hr />
                </div>
              </div>
              <div className="mt-5 flex justify-center">
                <Typography variant="h5" color={"blue"}>
                  <Link to="/signup" className="underline">
                    Signup
                  </Link>
                </Typography>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default Login;
